/**
 * Standard durations for UI updates. (Mostly CSS `transition`s.) All values
 * are in milliseconds.
 *
 * There’s a lot of conflicting claims about the ‘threshold of human
 * perception’, the minimum time the optical system needs to detect a change.
 * The values are anything from 16 ms (60 frames per second) to 80 ms or even
 * higher. `Timing` somewhat arbitrarily uses 80 ms as the `fastest`
 * duration.
 *
 * These durations pair well with `Transition`.
 *
 * @example
 * ...Shadow[ShadowElevation.E1],
 * ...Transition(['background-color'], {
 *   duration: Timing.Fast,
 * }),
 *
 * @note If you implement a `transition` manually rather than using
 *   `Transition`, or have an `animation` without a `Transition`, you should
 *   also include an `@media (prefers-reduced-motion: reduce)` rule, allowing
 *   users to disable the transition. (But you should use `Transition` instead,
 *   which makes this easier.)
 * @note These values have not been approved by the design team, and may change
 *   in the future. Try to use the values based on what feels right, rather
 *   than the specific number.
 * @note In the future the values may become user-configurable to improve
 *   accessibility. If this happens, the relationships between values will be
 *   retained.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-reduced-motion
 */
export const enum Timing {
  Fastest = 80,
  Fast = Fastest * 2,
  /**
   * @warning *Never* use `Debug` in production code, only for testing.
   */
  Debug = Fastest * 75,
  Default = Fast,
  /**
   * Specifically for use with `@opallabs/lapidary` `toast()`.
   */
  ToastAutoDismiss = 5000,
}
